import React from 'react';
import { 
  Typography, 
  Box,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert
} from '@mui/material';
import PageTemplate from '../components/PageTemplate';

const CookiePage: React.FC = () => {
  const cookieTypes = [
    {
      type: "Essential Cookies",
      purpose: "Required for basic website functionality",
      duration: "Session/1 year",
      examples: "User preferences, authentication state",
      canDisable: false
    },
    {
      type: "Analytics Cookies",
      purpose: "Help us understand how you use our website",
      duration: "1-2 years",
      examples: "Google Analytics, page views, user flow",
      canDisable: true
    },
    {
      type: "Advertising Cookies",
      purpose: "Used to deliver relevant advertisements",
      duration: "Up to 1 year",
      examples: "Google Ads, targeted advertising",
      canDisable: true
    },
    {
      type: "Functional Cookies",
      purpose: "Enable enhanced functionality and personalization",
      duration: "1 year",
      examples: "Theme preferences, language settings",
      canDisable: true
    }
  ];

  return (
    <PageTemplate 
      title="Cookie Policy" 
      description="Learn about how we use cookies and similar technologies on our website."
      breadcrumbs={[{ label: 'Cookie Policy', path: '/cookies' }]}
    >
      <Stack spacing={4}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Last updated:</strong> January 15, 2024
          </Typography>
        </Alert>

        <Box>
          <Typography variant="h4" gutterBottom>
            What Are Cookies?
          </Typography>
          <Typography variant="body1" paragraph>
            Cookies are small text files that are stored on your device when you visit our website. 
            They help us provide you with a better browsing experience by remembering your preferences 
            and analyzing how you use our site.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h4" gutterBottom>
            How We Use Cookies
          </Typography>
          <Typography variant="body1" paragraph>
            We use cookies for several purposes:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <li>
              <Typography variant="body1" paragraph>
                <strong>Essential functionality:</strong> To ensure our website works properly and securely
              </Typography>
            </li>
            <li>
              <Typography variant="body1" paragraph>
                <strong>Analytics:</strong> To understand how visitors interact with our website
              </Typography>
            </li>
            <li>
              <Typography variant="body1" paragraph>
                <strong>Personalization:</strong> To remember your preferences and settings
              </Typography>
            </li>
            <li>
              <Typography variant="body1" paragraph>
                <strong>Advertising:</strong> To show you relevant ads that may interest you
              </Typography>
            </li>
          </Box>
        </Box>

        <Box>
          <Typography variant="h4" gutterBottom>
            Types of Cookies We Use
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell><strong>Cookie Type</strong></TableCell>
                  <TableCell><strong>Purpose</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                  <TableCell><strong>Can Disable</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cookieTypes.map((cookie, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {cookie.type}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {cookie.examples}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {cookie.purpose}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {cookie.duration}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color={cookie.canDisable ? 'success.main' : 'warning.main'}
                        fontWeight={600}
                      >
                        {cookie.canDisable ? 'Yes' : 'No'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box>
          <Typography variant="h4" gutterBottom>
            Third-Party Cookies
          </Typography>
          <Typography variant="body1" paragraph>
            We may use third-party services that set their own cookies:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <li>
              <Typography variant="body1" paragraph>
                <strong>Google Analytics:</strong> Helps us understand website usage and performance
              </Typography>
            </li>
            <li>
              <Typography variant="body1" paragraph>
                <strong>Google Ads:</strong> Enables targeted advertising based on your interests
              </Typography>
            </li>
            <li>
              <Typography variant="body1" paragraph>
                <strong>Social Media Platforms:</strong> For social sharing functionality
              </Typography>
            </li>
          </Box>
        </Box>

        <Box>
          <Typography variant="h4" gutterBottom>
            Managing Your Cookie Preferences
          </Typography>
          <Typography variant="body1" paragraph>
            You have several options for managing cookies:
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Browser Settings
          </Typography>
          <Typography variant="body1" paragraph>
            Most web browsers allow you to control cookies through their settings. You can:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <li><Typography variant="body1">Block all cookies</Typography></li>
            <li><Typography variant="body1">Block third-party cookies only</Typography></li>
            <li><Typography variant="body1">Delete existing cookies</Typography></li>
            <li><Typography variant="body1">Set up notifications when cookies are sent</Typography></li>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Our Cookie Banner
          </Typography>
          <Typography variant="body1" paragraph>
            When you first visit our website, you'll see a cookie banner that allows you to:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <li><Typography variant="body1">Accept all cookies</Typography></li>
            <li><Typography variant="body1">Reject non-essential cookies</Typography></li>
            <li><Typography variant="body1">Customize your preferences</Typography></li>
          </Box>
        </Box>

        <Box>
          <Typography variant="h4" gutterBottom>
            Impact of Disabling Cookies
          </Typography>
          <Typography variant="body1" paragraph>
            While you can disable cookies, please note that this may affect your experience:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <li>
              <Typography variant="body1" paragraph>
                Some features may not work properly
              </Typography>
            </li>
            <li>
              <Typography variant="body1" paragraph>
                You may need to re-enter preferences each visit
              </Typography>
            </li>
            <li>
              <Typography variant="body1" paragraph>
                Personalized content may not be available
              </Typography>
            </li>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography variant="h4" gutterBottom>
            Changes to This Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Cookie Policy from time to time. Any changes will be posted on this page 
            with an updated revision date. We encourage you to review this policy periodically.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h4" gutterBottom>
            Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about our use of cookies, please contact us at:
          </Typography>
          <Typography variant="body1">
            Email: privacy@1timemail.com
          </Typography>
        </Box>
      </Stack>
    </PageTemplate>
  );
};

export default CookiePage;